import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2, Pause, Play, Search } from "lucide-react";
import type { CharacterVoiceCatalogueEntry } from "../../../../api/client";
import { cn } from "../../../../lib/utils";
import {
  useVoicePreview,
  type VoicePreviewController,
} from "./useVoicePreview";
import {
  buildVoiceCatalogueLanguageOptions,
  VOICE_CATALOGUE_AGE_OPTIONS,
  VOICE_CATALOGUE_GENDER_OPTIONS,
  voiceCatalogueAccentOptionsForLanguage,
} from "./voiceCatalogueFilters";

export interface VoiceCatalogPickerProps {
  voices: CharacterVoiceCatalogueEntry[];
  query: string;
  language: string;
  gender: string;
  age: string;
  accent: string;
  isLoading: boolean;
  error: string | null;
  selectionError?: string | null;
  hasMore: boolean;
  selectedVoiceRef?: string;
  selectingVoiceRef?: string | null;
  className?: string;
  resultsClassName?: string;
  heading?: string;
  showHeading?: boolean;
  showFilters?: boolean;
  selectLabel?: string;
  selectedLabel?: string;
  beforeResults?: React.ReactNode;
  voicePreview?: VoicePreviewController;
  onQueryChange: (value: string) => void;
  onLanguageChange: (value: string) => void;
  onGenderChange: (value: string) => void;
  onAgeChange: (value: string) => void;
  onAccentChange: (value: string) => void;
  onLoadMore: () => void;
  onRetry: () => void;
  onSelectVoice: (voice: CharacterVoiceCatalogueEntry) => void;
}

const VoiceCatalogPicker = memo(function VoiceCatalogPicker({
  voices,
  query,
  language,
  gender,
  age,
  accent,
  isLoading,
  error,
  selectionError,
  hasMore,
  selectedVoiceRef,
  selectingVoiceRef,
  className,
  resultsClassName,
  heading,
  showHeading = true,
  showFilters = true,
  selectLabel,
  selectedLabel,
  beforeResults,
  voicePreview,
  onQueryChange,
  onLanguageChange,
  onGenderChange,
  onAgeChange,
  onAccentChange,
  onLoadMore,
  onRetry,
  onSelectVoice,
}: VoiceCatalogPickerProps) {
  const { t } = useTranslation("canvas");
  const internalVoicePreview = useVoicePreview();
  const activeVoicePreview = voicePreview ?? internalVoicePreview;
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingVoiceRef, setPlayingVoiceRef] = useState<string | null>(null);
  const languageOptions = useMemo(
    () => buildVoiceCatalogueLanguageOptions(t),
    [t],
  );
  const accentOptions = useMemo(
    () => voiceCatalogueAccentOptionsForLanguage(language),
    [language],
  );
  const hasAccentOptions = accentOptions.length > 0;
  const accentIsValid =
    !accent || accentOptions.some((option) => option.value === accent);
  const accentValue = accentIsValid ? accent : "";

  const handleLanguageChange = useCallback(
    (nextLanguage: string) => {
      onLanguageChange(nextLanguage);
      const nextAccentOptions =
        voiceCatalogueAccentOptionsForLanguage(nextLanguage);
      if (
        accent &&
        !nextAccentOptions.some((option) => option.value === accent)
      ) {
        onAccentChange("");
      }
    },
    [accent, onAccentChange, onLanguageChange],
  );

  const handlePreview = useCallback(
    (voice: CharacterVoiceCatalogueEntry) => (event: React.MouseEvent) => {
      event.stopPropagation();
      if (!voice.preview_url) return;
      if (voicePreview) {
        activeVoicePreview.toggle(voice.voice_id, voice.preview_url);
        return;
      }
      if (playingVoiceRef === voice.voice_ref) {
        audioRef.current?.pause();
        audioRef.current = null;
        setPlayingVoiceRef(null);
        return;
      }

      audioRef.current?.pause();
      const audio = new Audio(voice.preview_url);
      audio.onended = () => setPlayingVoiceRef(null);
      audio.play().catch(() => setPlayingVoiceRef(null));
      audioRef.current = audio;
      setPlayingVoiceRef(voice.voice_ref);
    },
    [activeVoicePreview, playingVoiceRef, voicePreview],
  );

  useEffect(() => {
    return () => audioRef.current?.pause();
  }, []);

  useEffect(() => {
    if (!accentIsValid) {
      onAccentChange("");
    }
  }, [accentIsValid, onAccentChange]);

  return (
    <section
      className={cn(
        "rounded-lg border border-input-border bg-surface-0 p-3",
        className,
      )}
      data-testid="voice-catalog-picker"
    >
      {showHeading ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-medium uppercase tracking-wider text-text-muted">
            {heading ?? t("voiceTabContent.voiceCatalogue")}
          </p>
          {isLoading && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-text-muted" />
          )}
        </div>
      ) : null}

      {showFilters ? (
      <div className="mt-3 space-y-2">
        <label className="flex h-9 items-center gap-2 rounded-lg border border-input-border bg-input-bg px-2 text-text-muted focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
          <Search className="h-4 w-4" strokeWidth={2} />
          <input
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder={t("voiceTabContent.searchVoices")}
            className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-text-muted"
            data-testid="voice-catalog-search"
          />
        </label>

        <div className="grid grid-cols-2 gap-2">
          <select
            value={language}
            onChange={(event) => handleLanguageChange(event.target.value)}
            className="h-8 w-full rounded-lg border border-input-border bg-input-bg px-2 text-xs font-medium text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            data-testid="voice-catalog-language"
            aria-label={t("voiceTabContent.filterLanguage")}
          >
            {languageOptions.map((option) => (
              <option key={option.value || "any"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={gender}
            onChange={(event) => onGenderChange(event.target.value)}
            className="h-8 w-full rounded-lg border border-input-border bg-input-bg px-2 text-xs font-medium text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            data-testid="voice-catalog-gender"
            aria-label={t("voiceTabContent.filterGender")}
          >
            {VOICE_CATALOGUE_GENDER_OPTIONS.map((option) => (
              <option key={option.value || "any"} value={option.value}>
                {t(option.labelKey)}
              </option>
            ))}
          </select>
          <select
            value={age}
            onChange={(event) => onAgeChange(event.target.value)}
            className="h-8 w-full rounded-lg border border-input-border bg-input-bg px-2 text-xs font-medium text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            data-testid="voice-catalog-age"
            aria-label={t("voiceTabContent.filterAge")}
          >
            {VOICE_CATALOGUE_AGE_OPTIONS.map((option) => (
              <option key={option.value || "any"} value={option.value}>
                {t(option.labelKey)}
              </option>
            ))}
          </select>
          <select
            value={accentValue}
            onChange={(event) => onAccentChange(event.target.value)}
            disabled={!hasAccentOptions}
            className="h-8 w-full rounded-lg border border-input-border bg-input-bg px-2 text-xs font-medium text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="voice-catalog-accent"
            aria-label={t("voiceTabContent.filterAccent")}
          >
            <option value="">{t("voiceTabContent.filterAccentAny")}</option>
            {accentOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      ) : null}

      {error ? (
        <div className="mt-3 rounded-lg border border-destructive/30 bg-destructive/10 p-2">
          <p className="text-xs font-medium text-foreground">
            {t("voiceTabContent.voiceCatalogueFailed")}
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 h-7 rounded-lg border border-input-border bg-surface-1 px-2 text-xs font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3"
            data-testid="voice-catalog-retry"
          >
            {t("voiceTabContent.retry")}
          </button>
        </div>
      ) : null}

      {selectionError ? (
        <p className="mt-3 rounded-lg border border-destructive/30 bg-destructive/10 p-2 text-xs font-medium text-foreground">
          {selectionError}
        </p>
      ) : null}

      <div
        className={cn(
          "mt-3 max-h-56 space-y-2 overflow-y-auto pr-1",
          resultsClassName,
        )}
        data-testid="voice-catalog-results"
      >
        {beforeResults}
        {voices.map((voice) => {
          const selected = voice.voice_ref === selectedVoiceRef;
          const selecting = voice.voice_ref === selectingVoiceRef;
          const previewActive = voicePreview
            ? activeVoicePreview.state.voiceId === voice.voice_id
            : voice.voice_ref === playingVoiceRef;
          const playing = voicePreview
            ? previewActive &&
              (activeVoicePreview.state.phase === "playing" ||
                activeVoicePreview.state.phase === "loading")
            : previewActive;
          return (
            <div
              key={voice.voice_ref}
              className={cn(
                "rounded-lg border p-2 transition-colors",
                selected
                  ? "border-primary/40 bg-primary/10"
                  : "border-border-subtle bg-surface-1 hover:border-input-border-hover hover:bg-surface-2",
              )}
              data-testid="voice-catalog-row"
            >
              <div className="flex items-start gap-2">
                <button
                  type="button"
                  onClick={handlePreview(voice)}
                  disabled={!voice.preview_url}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-input-border bg-surface-0 text-text-muted transition-colors hover:border-input-border-hover hover:bg-surface-3 hover:text-foreground disabled:cursor-not-allowed disabled:hover:border-input-border disabled:hover:bg-surface-0 disabled:hover:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20"
                  aria-label={
                    playing
                      ? t("voiceTabContent.pauseCatalogueVoice", {
                          name: voice.name,
                        })
                      : t("voiceTabContent.previewCatalogueVoice", {
                          name: voice.name,
                        })
                  }
                  data-testid="voice-catalog-preview"
                >
                  {voicePreview &&
                  previewActive &&
                  activeVoicePreview.state.phase === "loading" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : playing ? (
                    <Pause
                      className="h-3.5 w-3.5"
                      fill="currentColor"
                      strokeWidth={2}
                    />
                  ) : (
                    <Play
                      className="ml-0.5 h-3.5 w-3.5"
                      fill="currentColor"
                      strokeWidth={2}
                    />
                  )}
                </button>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-xs font-semibold text-foreground">
                      {voice.name}
                    </p>
                    {voice.language ? (
                      <span className="shrink-0 rounded-full border border-input-border bg-input-bg px-1.5 py-0.5 text-xxs text-text-muted">
                        {voice.language}
                      </span>
                    ) : null}
                  </div>
                  {voice.description ? (
                    <p className="mt-1 line-clamp-2 text-xs font-medium leading-relaxed text-text-muted">
                      {voice.description}
                    </p>
                  ) : null}
                  <div className="mt-1 flex flex-wrap gap-1">
                    {voice.use_case ? (
                      <span className="rounded-full border border-input-border bg-input-bg px-1.5 py-0.5 text-xxs text-text-muted">
                        {voice.use_case}
                      </span>
                    ) : null}
                    {voice.descriptors.slice(0, 3).map((descriptor) => (
                      <span
                        key={descriptor}
                        className="rounded-full border border-input-border bg-input-bg px-1.5 py-0.5 text-xxs text-text-muted"
                      >
                        {descriptor}
                      </span>
                    ))}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => onSelectVoice(voice)}
                  disabled={selected || selecting}
                  className={cn(
                    "flex h-8 shrink-0 items-center gap-1 rounded-lg border px-2 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20",
                    selected
                      ? "border-primary/40 bg-primary/10 text-primary"
                      : "border-input-border bg-surface-0 text-foreground hover:border-input-border-hover hover:bg-surface-3 disabled:cursor-not-allowed disabled:text-text-muted disabled:hover:border-input-border disabled:hover:bg-surface-0",
                  )}
                  data-testid="voice-catalog-select"
                >
                  {selecting ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : selected ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : null}
                  {selected
                    ? (selectedLabel ?? t("voiceTabContent.selected"))
                    : (selectLabel ?? t("voiceTabContent.select"))}
                </button>
              </div>
            </div>
          );
        })}
        {!isLoading && !error && voices.length === 0 && !beforeResults ? (
          <p className="rounded-lg border border-border-subtle bg-surface-1 p-3 text-xs font-medium text-text-muted">
            {t("voiceTabContent.noVoicesFound")}
          </p>
        ) : null}
      </div>

      {hasMore ? (
        <button
          type="button"
          onClick={onLoadMore}
          disabled={isLoading}
          className="mt-3 h-8 w-full rounded-lg border border-input-border bg-surface-1 text-xs font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 disabled:cursor-not-allowed disabled:text-text-muted"
          data-testid="voice-catalog-load-more"
        >
          {isLoading
            ? t("voiceTabContent.loading")
            : t("voiceTabContent.loadMore")}
        </button>
      ) : null}
    </section>
  );
});

VoiceCatalogPicker.displayName = "VoiceCatalogPicker";

export { VoiceCatalogPicker };
